<script setup lang="ts">
import { usePlannerTemplateRepeats } from "@/composables/usePlannerTemplateRepeats";
import type { IMultiSelectItem } from "@/interfaces";
import { usePlannerTemplateForm } from "@/stores/plannerTemplateForm.module";
import FormGroup from "@/stories/form/FormGroup/FormGroup.vue";
import OptionSelectorInput from "@/stories/form/OptionSelectorInput/OptionSelectorInput.vue";
import SelectInput from "@/stories/form/SelectInput/SelectInput.vue";
import TimePicker from "@/stories/form/TimePicker/TimePicker.vue";
import type { PrimitiveModel, RruleOptions } from "@/types";
import TemplateRepeatRuleDialog from "@/views/planner/dialogs/templates/form/dialogs/TemplateRepeatRuleDialog.vue";
import FormContent from "@/views/planner/dialogs/templates/form/FormContent.vue";
import { ref } from "vue";

const store = usePlannerTemplateForm();
const defaultCadence = ref<number | undefined>(store.form.rruleToSave?.cadence);
const isRepeatModalOpen = ref(false);

const items: IMultiSelectItem[] = [
  { id: "5", title: "5", description: "min" },
  { id: "15", title: "15", description: "min" },
  { id: "30", title: "30", description: "min" },
  { id: "45", title: "45", description: "min" },
  { id: "60", title: "1", description: "time" },
  { id: "120", title: "2", description: "timer" },
  { id: "180", title: "3", description: "timer" },
  { id: "240", title: "4", description: "timer" },
];

const { possibleRepeatRuleOptions } = usePlannerTemplateRepeats();

const repetitionSelected = (value: PrimitiveModel) => {
  if (!value) {
    store.form.rruleToSave = undefined;
    defaultCadence.value = undefined;
  } else {
    defaultCadence.value = value as number;
    isRepeatModalOpen.value = true;
  }
};

const handleRepeatModalClose = () => {
  isRepeatModalOpen.value = false;
  defaultCadence.value = store.form.rruleToSave?.cadence;
};

const handleRepeatRuleUpdate = (value: RruleOptions) => {
  store.form.rruleToSave = value;
  isRepeatModalOpen.value = false;
};
</script>

<template>
  <FormContent>
    <div class="h600 text-neutral1000 pb-6">
      {{ $t("global.time") }}
    </div>
    <FormGroup>
      <TimePicker
        name="startTime"
        format="24h"
        :label="$t('global.time_start')"
        :validation="store.validation"
        v-model="store.form.startTime"
      />

      <OptionSelectorInput
        name="duration"
        :label="$t('templates.duration_of_the_task')"
        :items="items"
        :allowDeselect="false"
        v-model="store.form.duration"
      >
        <template #title="{ item }">
          <div class="flex justify-center items-center gap-1">
            <span class="h500">{{ item.title }}</span>
            <span class="b300 text-neutral700">{{ item.description }}</span>
          </div>
        </template>
      </OptionSelectorInput>

      <SelectInput
        name="repeat"
        :items="possibleRepeatRuleOptions"
        :label="$t('global.recurrence')"
        :autoDeselect="false"
        :modelValue="defaultCadence"
        @update:modelValue="repetitionSelected"
      />
    </FormGroup>
  </FormContent>

  <TemplateRepeatRuleDialog
    v-if="isRepeatModalOpen"
    storeName="PLANNER_TEMPLATE_FORM"
    :initialValue="{
      ...store.form.rruleToSave,
      cadence: defaultCadence || 7,
    }"
    @close="handleRepeatModalClose"
    @update="handleRepeatRuleUpdate"
  />
</template>
